import { useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import Modal from '../Modal'
import InputField from '../InputField'
import Select from '../Select'
import {
    UpdateEmployerModel,
    EmployerModel,
    EmployeeActivationType,
    EmployeeDataParserType,
    EmployeeDeactivationType,
} from '../../redux/types'
import DatePicker from '../DatePicker'
import S1 from '../typography/S1'
import Switch from '../Switch'

interface Props {
    initialValue?: EmployerModel
    onSaveClick?: (updateModel: UpdateEmployerModel) => void
    onCancelClick?: () => void
    visible: boolean
}

const EditEmployerInfoModal = ({
    initialValue,
    onSaveClick,
    onCancelClick,
    visible
}: Props) => {
    const styles = useStyles()
    const [employerInfo, setEmployerInfo] = useState<UpdateEmployerModel>()

    useEffect(() => {
        if (initialValue) {
            setEmployerInfo({
                appAbbreviation: initialValue.appAbbreviation,
                approvedCreditLimit: initialValue.approvedCreditLimit?.amount ?? 0,
                availableCreditLimit: initialValue.availableCreditLimit?.amount,
                remainingCreditSpaceWarningPercent: initialValue.remainingCreditSpaceWarningPercent,
                employeeActivationType: initialValue.employeeActivationType,
                employeeDeactivationType: initialValue.employeeDeactivationType,
                defaultPayoutTaxInPercent: initialValue.defaultPayoutTaxInPercent,
                defaultTaxTableIdentifier: initialValue.defaultTaxTableIdentifier,
                defaultFixedTaxRatePercent: initialValue.defaultFixedTaxRatePercent,
                payoutTaxImmutable: initialValue.payoutTaxImmutable,
                employeeDataParserType: initialValue.employeeDataParserType,
                defaultVacationPayPercent: initialValue.defaultVacationPayPercent,
                launchDate: initialValue.launchDate,
                newEmployeeMessageEnabled: initialValue.newEmployeeMessageEnabled,
                employeeActivationReminderEnabled: initialValue.employeeActivationReminderEnabled,
                employeeActivationReminderIntervalDays: initialValue.employeeActivationReminderIntervalDays,
                plannedShiftsEnabled: initialValue.plannedShiftsEnabled,
                openShiftsEnabled: initialValue.openShiftsEnabled,
                newEmployeesReportEnabled: initialValue.newEmployeesReportEnabled,
                freePayoutCountOnEmployeeActivation: initialValue.freePayoutCountOnEmployeeActivation,
                employerInitiatedPayoutsEnabled: initialValue.employerInitiatedPayoutsEnabled,
                employerInitiatedPayoutTaxPercent: initialValue.employerInitiatedPayoutTaxPercent,
                employerInitiatedPayoutClientConfiguration: initialValue.employerInitiatedPayoutClientConfiguration,
                employeeMinAgeInYears: initialValue.employeeMinAgeInYears,
                shiftSyncMaxDeletePercentage: initialValue.shiftSyncMaxDeletePercentage,
                shiftOrDeviationStaleWarningThreshold: initialValue.shiftOrDeviationStaleWarningThreshold,
                wfmSystem: initialValue.wfmSystem,
                hrSystem: initialValue.hrSystem,
                payrollAgency: initialValue.payrollAgency
            })
        }
    }, [initialValue])

    const handleOnSave = () => {
        if (employerInfo) {
            onSaveClick && onSaveClick(employerInfo)
        }
    }

    const isValid = () => {
        return (employerInfo?.approvedCreditLimit ?? 0) >= (employerInfo?.availableCreditLimit ?? 0)
    }

    const handleOnClose = () => {
        onCancelClick && onCancelClick()
    }

    const handleOnChange = (propName: string, value: any) => {
        if (employerInfo) {
            setEmployerInfo({...employerInfo, [propName]: value === '' ? null : value})
        }
    }

    const getEmployeeActivationTypeOptions = () => {
        return Object.keys(EmployeeActivationType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getEmployeeDeactivationTypeOptions = () => {
        return Object.keys(EmployeeDeactivationType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getEmployeeDataParserTypeOptions = () => {
        return Object.keys(EmployeeDataParserType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    return (
        <Modal
            title='Edit employer'
            show={visible}
            okButtonTitle='Save'
            cancelButtonTitle='Cancel'
            okButtonDisabled={!isValid()}
            onHide={() => handleOnClose()}
            onOkClick={() => handleOnSave()}
            onCancelClick={() => handleOnClose()}
        >
            <S1 className={styles.sectionTitle}>General</S1>
            <InputField
                id='appAbbreviation'
                value={employerInfo?.appAbbreviation ?? ''}
                onChange={(event) => handleOnChange('appAbbreviation', event.target.value)}
                label='Abbreviation in app'
                placeholder=''
              />
            <Select
                id='employeeActivationType'
                key='employeeActivationType'
                label='Employee activation type'
                options={getEmployeeActivationTypeOptions()}
                selectedValue={employerInfo?.employeeActivationType.toString()}
                onSelect={(value) => handleOnChange('employeeActivationType', value)}
            />
            <Select
                id='employeeDeactivationType'
                key='employeeDeactivationType'
                label='Employee deactivation type'
                options={getEmployeeDeactivationTypeOptions()}
                selectedValue={employerInfo?.employeeDeactivationType.toString()}
                onSelect={(value) => handleOnChange('employeeDeactivationType', value)}
            />
            <InputField
                id='defaultTaxTableIdentifier'
                value={employerInfo?.defaultTaxTableIdentifier?.toString() ?? ''}
                label='Default tax table'
                onChange={(event) => handleOnChange('defaultTaxTableIdentifier', event.target.value)}
                placeholder=''
            />
            <InputField
                id='defaultFixedTaxRatePercent'
                value={employerInfo?.defaultFixedTaxRatePercent?.toString() ?? ''}
                label='Default fix income tax in percent'
                onChange={(event) => handleOnChange('defaultFixedTaxRatePercent', event.target.value)}
                placeholder=''
            />
            <InputField
                id='defaultPayoutTaxInPercent'
                value={employerInfo?.defaultPayoutTaxInPercent.toString() ?? ''}
                label='Default payout tax in percent'
                onChange={(event) => handleOnChange('defaultPayoutTaxInPercent', event.target.value)}
                placeholder=''
            />
            <Switch
                id='payoutTaxImmutable'
                key='payoutTaxImmutable'
                checked={employerInfo?.payoutTaxImmutable ?? false}
                label='Payout tax immutable'
                onChange={() => handleOnChange('payoutTaxImmutable', !(employerInfo?.payoutTaxImmutable ?? false))}
            />
            <Select
                id='employeeDataParserType'
                key='employeeDataParserType'
                label='Employee data parser type'
                options={getEmployeeDataParserTypeOptions()}
                selectedValue={employerInfo?.employeeDataParserType.toString()}
                onSelect={(value) => handleOnChange('employeeDataParserType', value)}
            />
            <InputField
              id='defaultVacationPayPercent'
              numeric={true}
              value={employerInfo?.defaultVacationPayPercent?.toString() ?? ''}
              onChange={(event) => handleOnChange('defaultVacationPayPercent', event.target.value)}
              label='Default vacation pay percentage'
              placeholder=''
            />
            <InputField
                id='freePayoutCountOnEmployeeActivation'
                numeric={true}
                value={employerInfo?.freePayoutCountOnEmployeeActivation?.toString() ?? '0'}
                onChange={(event) => handleOnChange('freePayoutCountOnEmployeeActivation', event.target.value)}
                label='Free withdrawal count on activation'
                placeholder=''
            />
            <DatePicker
                id='launchDate'
                label='Launch date'
                includeTime={true}
                value={employerInfo?.launchDate}
                onChange={value => {
                    handleOnChange('launchDate', value)
                }}
            />
            <Switch
              id='newEmployeeMessageEnabled'
              key='newEmployeeMessageEnabled'
              checked={employerInfo?.newEmployeeMessageEnabled ?? false}
              label='Send welcome message to new employees'
              onChange={() => handleOnChange('newEmployeeMessageEnabled', !(employerInfo?.newEmployeeMessageEnabled ?? false))}
            />
            <Switch
              id='employeeActivationReminderEnabled'
              key='employeeActivationReminderEnabled'
              checked={employerInfo?.employeeActivationReminderEnabled ?? false}
              label='Send activation reminders to employees'
              onChange={() => handleOnChange('employeeActivationReminderEnabled', !(employerInfo?.employeeActivationReminderEnabled ?? false))}
            />
            <InputField
              id='employeeActivationReminderIntervalDays'
              numeric={true}
              value={employerInfo?.employeeActivationReminderIntervalDays?.toString() ?? ''}
              onChange={(event) => handleOnChange('employeeActivationReminderIntervalDays', event.target.value)}
              label='Activation reminder interval (days)'
              placeholder=''
            />
            <Switch
              id='plannedShiftsEnabled'
              key='plannedShiftsEnabled'
              checked={employerInfo?.plannedShiftsEnabled ?? false}
              label='Planned shifts enabled'
              onChange={() => handleOnChange('plannedShiftsEnabled', !(employerInfo?.plannedShiftsEnabled ?? false))}
            />
            <Switch
              id='openShiftsEnabled'
              key='openShiftsEnabled'
              checked={employerInfo?.openShiftsEnabled ?? false}
              label='Open shifts enabled'
              onChange={() => handleOnChange('openShiftsEnabled', !(employerInfo?.openShiftsEnabled ?? false))}
            />
            <Switch
              id='newEmployeesReportEnabled'
              key='newEmployeesReportEnabled'
              checked={employerInfo?.newEmployeesReportEnabled ?? false}
              label='New employees report enabled'
              onChange={() => handleOnChange('newEmployeesReportEnabled', !(employerInfo?.newEmployeesReportEnabled ?? false))}
            />
            <Switch
              id='employerInitiatedPayoutsEnabled'
              key='employerInitiatedPayoutsEnabled'
              checked={employerInfo?.employerInitiatedPayoutsEnabled ?? false}
              label='Employer initiated payouts enabled'
              onChange={() => handleOnChange('employerInitiatedPayoutsEnabled', !(employerInfo?.employerInitiatedPayoutsEnabled ?? false))}
            />
            <InputField
              id='employerInitiatedPayoutTaxPercent'
              numeric={true}
              value={employerInfo?.employerInitiatedPayoutTaxPercent?.toString() ?? ''}
              onChange={(event) => handleOnChange('employerInitiatedPayoutTaxPercent', event.target.value)}
              label='Employer initiated payout tax percent (0-100)'
              placeholder=''
            />
            <InputField
              id='employeeMinAgeInYears'
              numeric={true}
              value={employerInfo?.employeeMinAgeInYears?.toString() ?? ''}
              onChange={(event) => handleOnChange('employeeMinAgeInYears', event.target.value)}
              label='Employee min age'
              placeholder=''
            />
            <InputField
              id='shiftSyncMaxDeletePercentage'
              numeric={true}
              value={employerInfo?.shiftSyncMaxDeletePercentage?.toString() ?? ''}
              onChange={(event) => handleOnChange('shiftSyncMaxDeletePercentage', event.target.value)}
              label='Shift sync max delete percentage'
              placeholder=''
            />
            <InputField
              id='shiftOrDeviationStaleWarningThreshold'
              value={employerInfo?.shiftOrDeviationStaleWarningThreshold?.toString() ?? ''}
              onChange={(event) => handleOnChange('shiftOrDeviationStaleWarningThreshold', event.target.value)}
              label='Stale sync threshold'
              placeholder='00:00:00'
            />
            <InputField
              id='wfmSystem'
              value={employerInfo?.wfmSystem ?? ''}
              onChange={(event) => handleOnChange('wfmSystem', event.target.value)}
              label='WFM system'
              placeholder=''
            />
            <InputField
              id='hrSystem'
              value={employerInfo?.hrSystem ?? ''}
              onChange={(event) => handleOnChange('hrSystem', event.target.value)}
              label='HR system'
              placeholder=''
            />
            <InputField
              id='payrollAgency'
              value={employerInfo?.payrollAgency ?? ''}
              onChange={(event) => handleOnChange('payrollAgency', event.target.value)}
              label='Payroll agency'
              placeholder=''
            />
            <div style={{marginTop: 30}}>
                <S1 className={styles.sectionTitle}>Credit settings</S1>
                <InputField
                    id='approvedCreditLimit'
                    value={employerInfo?.approvedCreditLimit?.toString() ?? ''}
                    label={`Approved credit (${initialValue?.effectivePayoutSettings?.currency?.toUpperCase()})`}
                    onChange={(event) => handleOnChange('approvedCreditLimit', event.target.value)}
                    placeholder=''
                />
                <InputField
                    id='availableCreditLimit'
                    valid={(employerInfo?.approvedCreditLimit ?? 0) >= (employerInfo?.availableCreditLimit ?? 0)}
                    value={employerInfo?.availableCreditLimit?.toString() ?? ''}
                    label={`Allocated credit (${initialValue?.effectivePayoutSettings?.currency?.toUpperCase()})`}
                    onChange={(event) => handleOnChange('availableCreditLimit', event.target.value)}
                    placeholder=''
                />
                <InputField
                    id='remainingCreditSpaceWarningPercent'
                    numeric={true}
                    value={employerInfo?.remainingCreditSpaceWarningPercent?.toString() ?? ''}
                    onChange={(event) => handleOnChange('remainingCreditSpaceWarningPercent', event.target.value)}
                    label='Remaining credit space warning percent'
                    placeholder=''
                />
            </div>
        </Modal>
    )
}

export default EditEmployerInfoModal

const useStyles = createUseStyles({
    container: {
        alignItems: 'center',
        textAlign: 'center',
        padding: '12px 20px',
        margin: '10px 0',
    },
    sectionTitle: {
        marginBottom: 15
    }
})
